import { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { DUCK_CHAT_MAX_WIDTH } from "duck/ui/constants";
import type { LexicalEditor } from "lexical";
import { useTranslation } from "react-i18next";
import ArrowCircleUpOutlinedIcon from "@mui/icons-material/ArrowCircleUpOutlined";
import MicNoneOutlinedIcon from "@mui/icons-material/MicNoneOutlined";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import Box from "@mui/material/Box";

import { useConfigContext } from "shared/contexts/ConfigContext";
import { useViewport } from "shared/contexts/ViewportContext";
import { formatBytes } from "shared/locale/format";

import { VIADUCK_PAGE_CONFIG_KEY } from "pages/SuperAdmin/PagesConfig/types";

import IconButton from "features/components/IconButton";
import Typography from "features/components/Typography";
import { toast } from "features/ui/Toast";

import ChatFileUpload from "./components/ChatFileUpload";
import DirectiveComposer from "./components/DirectiveComposer";
import type { DirectiveComposerState } from "./components/DirectiveComposer.types";
import { getDirectiveComposerSubmitText } from "./components/DirectiveComposer.utils";
import SafeModeToggle from "./components/SafeModeToggle";
import SpeechToTextOverlay from "./components/SpeechToTextOverlay";
import Suggestions from "./components/Suggestions";
import ThreadLogURL from "./components/threads/ThreadLogURL";
import UploadedFile from "./components/UploadedFile";
import {
  CHAT_FILE_AVAILABLE_TOKENS,
  CHAT_FILE_TEXT_BYTES_PER_TOKEN,
} from "./constants";
import { useChatContext } from "./contexts/useChatContext";
import { useSpeechToText } from "./hooks/useSpeechToText";
import { estimateTotalFileTokens } from "./utils";

const getFileDeduplicationKey = (file: File) =>
  `${file.name}-${file.size}-${file.type}-${file.lastModified}`;

const ChatInput = () => {
  const [inputValue, setInputValue] = useState("");
  const [composerState, setComposerState] = useState<DirectiveComposerState>({
    hasContent: false,
    text: "",
  });
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const editorRef = useRef<LexicalEditor | null>(null);
  // Synchronous guard so two rapid submits (double Enter, double suggestion
  // click) cannot run the async submit pipeline concurrently and double-send.
  const isSubmittingRef = useRef(false);
  const { pages } = useConfigContext();
  const { t } = useTranslation("duck");
  const { isMobile, resetViewportScale } = useViewport();
  const {
    messages,
    isStreaming,
    isAnySubthreadStreaming,
    streamingSubthreadCount,
    isLoadingCurrentThread,
    sendMessage,
    stopStreaming,
    followupSuggestions,
    isFetchingFollowups,
    refreshFollowupSuggestions,
    focusInput,
    inputRef,
    currentThreadId,
  } = useChatContext();

  const [expanded, setExpanded] = useState(!messages.length && !isMobile);

  // Block input when main chat or any subthread is streaming, or when loading thread messages
  const isInputBlocked =
    isStreaming || isAnySubthreadStreaming || isLoadingCurrentThread;

  const viaDuckConfig = pages[VIADUCK_PAGE_CONFIG_KEY];
  const initialSuggestions = viaDuckConfig?.initialSuggestions || [];
  const speechToTextEnabled = viaDuckConfig?.speechToTextEnabled ?? false;
  const autoSubmitMode = viaDuckConfig?.autoSubmitMode ?? true;
  const isPressToSubmit = !autoSubmitMode;

  const {
    showOverlay: showSpeechToTextOverlay,
    sttAvailable,
    handleMicClick,
    micTooltip,
    overlayProps,
    stopPressToSubmitRecording,
  } = useSpeechToText({
    isPressToSubmit,
    speechToTextEnabled,
    isInputBlocked,
    sendMessage,
    setInputValue,
    inputValue,
  });

  const pressToSubmitSpeechToTextActive =
    showSpeechToTextOverlay && isPressToSubmit;

  // Expand input when switching to a new/empty thread
  useEffect(() => {
    if (!messages.length && !isMobile) {
      setExpanded(true);
    }
  }, [currentThreadId, isMobile]); // eslint-disable-line react-hooks/exhaustive-deps

  // Force expanded state when press-to-submit STT is active
  useEffect(() => {
    if (pressToSubmitSpeechToTextActive) {
      setExpanded(true);
    }
  }, [pressToSubmitSpeechToTextActive]);

  // --- Expanded / collapsed input layout ---

  const handleExpand = () => {
    setExpanded(true);
  };

  const handleInputBlur = () => {
    if (
      !composerState.hasContent &&
      selectedFiles.length === 0 &&
      !pressToSubmitSpeechToTextActive
    ) {
      setExpanded(false);
    }

    resetViewportScale();
  };

  // Expand when files are added
  useEffect(() => {
    if (selectedFiles.length > 0) {
      setExpanded(true);
    }
  }, [selectedFiles.length]);

  const handleSubmit = async (explicitValue?: string) => {
    // Drop the submit if one is already in flight. Checked and
    // set synchronously before any await so a second call cannot slip through.
    if (isSubmittingRef.current) return;

    isSubmittingRef.current = true;
    try {
      const value =
        explicitValue ?? getDirectiveComposerSubmitText(editorRef.current);

      if (!value.trim()) return;

      stopPressToSubmitRecording();

      const attachments = selectedFiles.length ? selectedFiles : undefined;
      const didSend = await sendMessage(value, undefined, attachments);
      if (didSend) {
        setInputValue("");
        setSelectedFiles([]);
        // Keep the composer focused after submitting
        focusInput();
      }
    } finally {
      isSubmittingRef.current = false;
    }
  };

  const handleRemoveSelectedFile = (indexToRemove: number) => {
    setSelectedFiles((prev) =>
      prev.filter((_, fileIndex) => fileIndex !== indexToRemove)
    );
  };

  const handleSelectFiles = async (newFiles: File[]): Promise<void> => {
    const existingKeys = new Set(selectedFiles.map(getFileDeduplicationKey));
    const dedupedNewFiles = newFiles.filter((file) => {
      const key = getFileDeduplicationKey(file);
      if (existingKeys.has(key)) return false;

      existingKeys.add(key);

      return true;
    });

    if (dedupedNewFiles.length === 0) return;

    const allFiles = [...selectedFiles, ...dedupedNewFiles];
    const promptTokens = Math.ceil(
      new TextEncoder().encode(inputValue).length /
        CHAT_FILE_TEXT_BYTES_PER_TOKEN
    );

    let fileTokens: number;
    try {
      fileTokens = await estimateTotalFileTokens(allFiles);
    } catch (error) {
      console.error("Failed to estimate file tokens:", error);
      const message = error instanceof Error ? error.message : String(error);
      toast.error(t("unable_to_process_files", { message }));

      return;
    }

    const totalTokens = fileTokens + promptTokens;

    if (totalTokens > CHAT_FILE_AVAILABLE_TOKENS) {
      const bytesToRemove =
        (totalTokens - CHAT_FILE_AVAILABLE_TOKENS) *
        CHAT_FILE_TEXT_BYTES_PER_TOKEN;
      const totalBytes = allFiles.reduce((sum, f) => sum + f.size, 0);
      toast.error(
        t("files_exceed_context_limit", {
          totalSize: formatBytes(totalBytes, t),
          removeSize: formatBytes(bytesToRemove, t),
        })
      );

      return;
    }

    setSelectedFiles(allFiles);
  };

  const handleSuggestionClick = (suggestion: string) => {
    if (!suggestion || isInputBlocked) return;

    handleSubmit(suggestion);
    focusInput();
  };

  const hasMessages = messages.length > 0;
  const showInitialSuggestions =
    !hasMessages && !isInputBlocked && !isLoadingCurrentThread;
  const showFollowupSuggestions = hasMessages && followupSuggestions.length > 0;

  // Determine which right-side button to show in text input mode
  const renderRightButton = () => {
    if (isStreaming) {
      return (
        <IconButton
          onClick={stopStreaming}
          color="transparent"
          size="small"
          data-testid="duck-stop-generation-cta"
          tooltip={t("stop")}
        >
          <StopCircleOutlinedIcon sx={{ fontSize: 24 }} />
        </IconButton>
      );
    }

    if (sttAvailable && !composerState.hasContent) {
      return (
        <IconButton
          onClick={handleMicClick}
          color="transparent"
          size="small"
          disabled={isInputBlocked}
          data-testid="duck-conversation-mode-toggle"
          tooltip={micTooltip}
        >
          <MicNoneOutlinedIcon fontSize="small" />
        </IconButton>
      );
    }

    return (
      <IconButton
        onClick={() => handleSubmit()}
        color="transparent"
        size="small"
        disabled={!composerState.hasContent || isAnySubthreadStreaming}
        data-testid="duck-submit-utterance-cta"
        tooltip={t("send")}
      >
        <ArrowCircleUpOutlinedIcon sx={{ fontSize: 24 }} />
      </IconButton>
    );
  };

  return (
    <Box
      className={classNames(
        "p-4 w-full m-0 border-none bg-transparent mt-auto flex flex-col items-center",
        {
          "mb-[40vh]": messages.length === 0 && !isMobile,
        }
      )}
    >
      <div
        className="gap-2 flex flex-col w-full"
        style={{ maxWidth: DUCK_CHAT_MAX_WIDTH }}
      >
        {showInitialSuggestions && (
          <Suggestions
            suggestions={initialSuggestions}
            isStreaming={isInputBlocked}
            onSuggestionClick={handleSuggestionClick}
            maxShownLimit={2}
            refreshTestId="duck-refresh-initial-suggestions-cta"
          />
        )}
        {showFollowupSuggestions && (
          <Suggestions
            suggestions={followupSuggestions}
            isStreaming={isInputBlocked}
            onSuggestionClick={handleSuggestionClick}
            onRefresh={refreshFollowupSuggestions}
            isRefreshing={isFetchingFollowups}
            refreshTestId="duck-refresh-followups-cta"
          />
        )}
        <Box className="border border-gray-100 rounded-lg shadow-md">
          {showSpeechToTextOverlay && !isPressToSubmit ? (
            <SpeechToTextOverlay {...overlayProps} />
          ) : (
            <>
              {(expanded || pressToSubmitSpeechToTextActive) && (
                <Box className="px-3 pt-3">
                  <DirectiveComposer
                    disabled={pressToSubmitSpeechToTextActive}
                    editorRef={editorRef}
                    inputRef={inputRef}
                    placeholder={t("placeholder")}
                    value={inputValue}
                    onBlur={handleInputBlur}
                    onChange={(state) => {
                      setComposerState(state);
                      setInputValue(state.text);
                    }}
                    onSubmit={() => {
                      if (!isInputBlocked) {
                        handleSubmit();
                      }
                    }}
                  />
                </Box>
              )}
              {pressToSubmitSpeechToTextActive ? (
                <SpeechToTextOverlay {...overlayProps} />
              ) : (
                <Box className="flex items-center gap-1 p-2">
                  <ChatFileUpload onSelect={handleSelectFiles} />
                  <ThreadLogURL />
                  <SafeModeToggle />
                  {selectedFiles.length > 0 && (
                    <Box className="flex flex-wrap gap-1">
                      {selectedFiles.map((selectedFile, index) => (
                        <UploadedFile
                          key={getFileDeduplicationKey(selectedFile)}
                          fileName={selectedFile.name}
                          onClear={() => handleRemoveSelectedFile(index)}
                        />
                      ))}
                    </Box>
                  )}
                  {!expanded && (
                    <button
                      onClick={handleExpand}
                      className="h-7.5 flex-1 flex flex-row items-center justify-start"
                    >
                      <Typography
                        className="px-1 select-none text-gray-400 text-left"
                        variant="body1"
                        data-testid="duck-text-input"
                      >
                        {t("placeholder")}
                      </Typography>
                    </button>
                  )}
                  <Box className="ml-auto flex items-center gap-1">
                    {isAnySubthreadStreaming && !isStreaming && (
                      <Typography
                        variant="caption"
                        className="italic"
                        color="text.secondary"
                      >
                        {t("subthreads_still_running", {
                          count: streamingSubthreadCount,
                        })}
                      </Typography>
                    )}
                    {renderRightButton()}
                  </Box>
                </Box>
              )}
            </>
          )}
        </Box>
      </div>
    </Box>
  );
};

export default ChatInput;
